import { CalendarDays, ChevronDown, Fingerprint, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ACTOR_TYPE_LABELS, eventEntityLabel, eventTypeSentence, type EventLogFacets } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { DatePicker } from '../../../../components/DatePicker';
import { Input, SearchInput } from '../../../../components/Input';
import { Popover, usePopover } from '../../../../components/Popover';
import { addDhakaDays, dhakaToday } from '../../../../lib/dhaka-time';
import { formatNumber } from '../../../../lib/format';
import { filterCount, isWholeId, whenLabel, whenTail, withFilters, type EventLogSearch, type EventLogWindow } from './event-log-address';
import { shortId } from './event-log-words';
import { FilterPill } from './FilterPill';
import styles from './EventLogFilters.module.css';

// Event log A's filter row: the search, then When (Today by default), Entity, Event type, Who did it, Venture and
// Record id as pills whose choices open beside them, "Clear N filters", and the count on the right. On a phone the
// pills fold behind one "Filters" button.

/** Typing settles for this long before the table is asked again (06-qol §6). */
const TYPING_SETTLE_MS = 250;

interface EventLogFiltersProps {
  readonly search: EventLogSearch;
  readonly facets: EventLogFacets | undefined;
  /** The count for the filters, or undefined while it loads. */
  readonly total: number | undefined;
  readonly isTotalCapped: boolean;
  /** The name of the person "Everything by" picked, from the rows, for the Who pill. */
  readonly actorName: string | null;
  readonly onChange: (next: EventLogSearch) => void;
}

export function EventLogFilters({ search, facets, total, isTotalCapped, actorName, onChange }: EventLogFiltersProps): ReactNode {
  const set = (patch: Parameters<typeof withFilters>[1]): void => onChange(withFilters(search, patch));
  const active = filterCount(search);
  const [isOpenOnPhone, setOpenOnPhone] = useState(false);

  const entityOptions = (facets?.entityTypes ?? []).map((value) => ({ value, label: eventEntityLabel(value), code: value }));
  const typeOptions = (facets?.eventTypes ?? []).map((value) => ({ value, label: eventTypeSentence(value), code: value }));
  const ventureOptions = (facets?.ventures ?? []).map((venture) => ({ value: venture.id, label: venture.name }));
  const whoOptions = [
    { value: 'user', label: ACTOR_TYPE_LABELS.user },
    { value: 'system', label: ACTOR_TYPE_LABELS.system },
    ...(search.actorId ? [{ value: `person:${search.actorId}`, label: actorName ?? 'The person picked' }] : []),
  ];
  const whoValue = search.actorId ? `person:${search.actorId}` : search.actorType;

  return (
    <div className={styles.filters}>
      <div className={styles.top}>
        <FilterSearch q={search.q} onSearch={(q) => set({ q })} />
        <button
          type="button"
          className={styles.phoneToggle}
          aria-expanded={isOpenOnPhone}
          onClick={() => setOpenOnPhone((open) => !open)}
        >
          <SlidersHorizontal size={15} aria-hidden />
          Filters
          {active > 0 && <span className={styles.badge}>{active}</span>}
        </button>
      </div>

      <div className={styles.pills} data-open={isOpenOnPhone || undefined}>
        <WhenPill search={search} onChange={set} />
        <FilterPill label="Entity" anyLabel="Any" options={entityOptions} value={search.entityType} onChange={(entityType) => set({ entityType })} />
        <FilterPill label="Event type" anyLabel="Any" options={typeOptions} value={search.eventType} onChange={(eventType) => set({ eventType })} />
        <FilterPill
          label="Who did it"
          anyLabel="Anyone"
          options={whoOptions}
          value={whoValue}
          onChange={(value) => {
            if (value?.startsWith('person:')) return;
            set({ actorType: value === 'user' || value === 'system' ? value : undefined, actorId: undefined });
          }}
        />
        <FilterPill label="Venture" anyLabel="All" options={ventureOptions} value={search.ventureId} onChange={(ventureId) => set({ ventureId })} />
        <RecordIdPill entityId={search.entityId} onChange={(entityId) => set({ entityId })} />
        {active > 0 && (
          <Button size="sm" variant="ghost" icon={X} onClick={() => onChange({})}>
            Clear {active} {active === 1 ? 'filter' : 'filters'}
          </Button>
        )}
        <span className={styles.count} aria-live="polite">
          {total === undefined ? '' : `${formatNumber(total)}${isTotalCapped ? '+' : ''} ${total === 1 && !isTotalCapped ? 'event' : 'events'}${whenTail(search)}`}
        </span>
      </div>
    </div>
  );
}

function FilterSearch({ q, onSearch }: { readonly q: string | undefined; readonly onSearch: (q: string | undefined) => void }): ReactNode {
  const [draft, setDraft] = useState(q ?? '');
  const [seen, setSeen] = useState(q);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The address changed some other way (Clear filters, Back): the box follows it.
  if (seen !== q) {
    setSeen(q);
    setDraft(q ?? '');
  }
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const change = (next: string): void => {
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearch(next.trim() === '' ? undefined : next.trim()), TYPING_SETTLE_MS);
  };
  return (
    <div className={styles.search}>
      <SearchInput
        aria-label="Search names, events, values"
        placeholder="Search names, events, values"
        value={draft}
        onChange={(event) => change(event.target.value)}
        onClear={() => change('')}
        data-primary-search
      />
    </div>
  );
}

const WINDOWS: readonly { readonly value: EventLogWindow; readonly label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: 'all', label: 'Any time' },
];

/** "When": Today, the last 7 or 30 days, any time, or two Dhaka days. Opens beside its pill. */
function WhenPill({ search, onChange }: { readonly search: EventLogSearch; readonly onChange: (patch: Parameters<typeof withFilters>[1]) => void }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const today = dhakaToday();
  const [from, setFrom] = useState(search.from ?? '');
  const [to, setTo] = useState(search.to ?? '');
  const isRange = search.from !== undefined || search.to !== undefined;
  const isBackwards = from !== '' && to !== '' && to < from;
  const isDefault = !isRange && search.when === undefined;

  const open = (): void => {
    setFrom(search.from ?? addDhakaDays(today, -6));
    setTo(search.to ?? today);
    toggle();
  };
  const pickWindow = (value: EventLogWindow): void => {
    onChange({ when: value === 'today' ? undefined : value });
    close();
  };
  const applyRange = (): void => {
    if (isBackwards) return;
    onChange({ from: from === '' ? undefined : from, to: to === '' ? undefined : to });
    close();
  };

  return (
    <>
      <button ref={triggerRef} type="button" className={styles.whenPill} data-set={!isDefault || undefined} onClick={open} aria-haspopup="dialog" aria-expanded={anchor !== null}>
        <CalendarDays size={14} aria-hidden />
        <b>{whenLabel(search)}</b>
        <ChevronDown size={14} aria-hidden />
      </button>
      <Popover anchor={anchor} onClose={close} label="When">
        <div className={styles.whenPanel}>
          <div className={styles.windows}>
            {WINDOWS.map((window) => (
              <button
                key={window.value}
                type="button"
                className={styles.window}
                aria-pressed={!isRange && (search.when ?? 'today') === window.value}
                onClick={() => pickWindow(window.value)}
              >
                {window.label}
              </button>
            ))}
          </div>
          <p className={styles.panelHeading}>Or pick the dates</p>
          <div className={styles.dates}>
            <label className={styles.dateField}>
              <span className={styles.dateLabel}>From</span>
              <DatePicker label="From" value={from} max={today} onChange={setFrom} />
            </label>
            <label className={styles.dateField}>
              <span className={styles.dateLabel}>Until</span>
              <DatePicker label="Until" value={to} max={today} onChange={setTo} isInvalid={isBackwards} />
            </label>
          </div>
          {isBackwards && <p className={styles.error}>Until can&apos;t be before From.</p>}
          <p className={styles.hint}>Days run midnight to midnight in Dhaka.</p>
          <div className={styles.panelActions}>
            <Button variant="solid" size="sm" onClick={applyRange} disabled={isBackwards || (from === '' && to === '')}>
              Show these dates
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}

/**
 * "Record id": everything that ever happened to one record. Only a whole id is a filter — a part of one used to count
 * as a filter and was never sent, so "Clear 1 filter" sat over an unfiltered list.
 */
function RecordIdPill({ entityId, onChange }: { readonly entityId: string | undefined; readonly onChange: (entityId: string | undefined) => void }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const [draft, setDraft] = useState(entityId ?? '');
  const [isTried, setTried] = useState(false);
  const isValid = isWholeId(draft);

  const open = (): void => {
    setDraft(entityId ?? '');
    setTried(false);
    toggle();
  };
  const apply = (): void => {
    setTried(true);
    if (draft.trim() === '') {
      onChange(undefined);
      close();
      return;
    }
    if (!isValid) return;
    onChange(draft.trim().toLowerCase());
    close();
  };

  return (
    <>
      <button ref={triggerRef} type="button" className={styles.whenPill} data-set={entityId !== undefined || undefined} onClick={open} aria-haspopup="dialog" aria-expanded={anchor !== null}>
        <Fingerprint size={14} aria-hidden />
        {entityId ? (
          <span>
            Record: <b className={styles.mono}>{shortId(entityId)}</b>
          </span>
        ) : (
          <span>Record id</span>
        )}
        <ChevronDown size={14} aria-hidden />
      </button>
      <Popover anchor={anchor} onClose={close} label="Record id">
        <form
          className={styles.whenPanel}
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <label className={styles.dateField}>
            <span className={styles.dateLabel}>Everything that happened to one record</span>
            <Input
              aria-label="Record id"
              placeholder="Paste a record id"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              isInvalid={isTried && draft.trim() !== '' && !isValid}
              autoFocus
            />
          </label>
          {isTried && draft.trim() !== '' && !isValid && <p className={styles.error}>Paste the whole id: 36 letters, numbers and dashes.</p>}
          <div className={styles.panelActions}>
            {entityId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onChange(undefined);
                  close();
                }}
              >
                Remove
              </Button>
            )}
            <Button variant="solid" size="sm" type="submit">
              Show this record
            </Button>
          </div>
        </form>
      </Popover>
    </>
  );
}
